import React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from './ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';

/**
 * Reusable pagination controls for CRM list pages.
 *
 * Props:
 *  - page (1-indexed current page)
 *  - pageSize (10|25|50)
 *  - totalItems (full unpaginated count)
 *  - onPageChange(newPage)
 *  - onPageSizeChange(newSize)
 *  - testidPrefix='pagination' for E2E test IDs
 *  - itemLabel='item'
 */
export const Pagination = ({
  page,
  pageSize,
  totalItems,
  onPageChange,
  onPageSizeChange,
  testidPrefix = 'pagination',
  itemLabel = 'item',
}) => {
  const totalPages = Math.max(1, Math.ceil((totalItems || 0) / pageSize));
  const safePage = Math.min(Math.max(1, page), totalPages);
  const startIdx = totalItems === 0 ? 0 : (safePage - 1) * pageSize + 1;
  const endIdx = Math.min(safePage * pageSize, totalItems);

  return (
    <div
      className="flex flex-wrap items-center justify-between gap-3 mt-4 px-1"
      data-testid={`${testidPrefix}-bar`}
    >
      <div className="text-xs text-[#6B6B7A]" data-testid={`${testidPrefix}-indicator`}>
        {totalItems === 0
          ? `No ${itemLabel}s`
          : `Showing ${startIdx}-${endIdx} of ${totalItems} ${itemLabel}${totalItems === 1 ? '' : 's'}`}
        {' · '}
        <span className="font-medium text-[#1E1E24]">
          Page {safePage} of {totalPages}
        </span>
      </div>
      <div className="flex items-center gap-2">
        <span className="text-xs text-[#6B6B7A]">Rows per page:</span>
        <Select
          value={String(pageSize)}
          onValueChange={(v) => onPageSizeChange(parseInt(v, 10))}
        >
          <SelectTrigger
            className="h-8 w-20 rounded-full bg-white border-[#E8E5DF] text-xs"
            data-testid={`${testidPrefix}-page-size`}
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="10">10</SelectItem>
            <SelectItem value="25">25</SelectItem>
            <SelectItem value="50">50</SelectItem>
          </SelectContent>
        </Select>
        <Button
          variant="outline"
          size="sm"
          onClick={() => onPageChange(safePage - 1)}
          disabled={safePage <= 1}
          className="rounded-full border-[#E8E5DF] h-8 px-3"
          data-testid={`${testidPrefix}-prev`}
        >
          <ChevronLeft className="w-3.5 h-3.5 mr-1" /> Prev
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => onPageChange(safePage + 1)}
          disabled={safePage >= totalPages}
          className="rounded-full border-[#E8E5DF] h-8 px-3"
          data-testid={`${testidPrefix}-next`}
        >
          Next <ChevronRight className="w-3.5 h-3.5 ml-1" />
        </Button>
      </div>
    </div>
  );
};

/**
 * Slice the full list by current page/pageSize. Returns the paged subset
 * (call this AFTER filtering/sorting client-side so pagination is the
 * very last step).
 */
export const paginate = (items, page, pageSize) => {
  const safePage = Math.max(1, page);
  const start = (safePage - 1) * pageSize;
  return (items || []).slice(start, start + pageSize);
};

/**
 * After a deletion: if the user is on the last page and that page is now
 * empty, slide them back to the previous non-empty page. Otherwise keep
 * them where they are.
 */
export const reconcilePageAfterDelete = (
  remainingTotal,
  currentPage,
  pageSize,
) => {
  const totalPages = Math.max(1, Math.ceil((remainingTotal || 0) / pageSize));
  if (currentPage > totalPages) return totalPages;
  return currentPage;
};

export default Pagination;
